import { useEffect, useRef } from 'react';
import type { Tool } from './feedback-draw';

interface Options {
  readonly enabled: boolean;
  readonly tools: ReadonlyArray<{ readonly id: Tool; readonly key: string }>;
  readonly onTool: (tool: Tool) => void;
  readonly onUndo: () => void;
  /** Null while nothing is selected: Delete and Backspace then stay with the page. */
  readonly onDelete: (() => void) | null;
}

const typing = (target: EventTarget | null) => target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement;

/** Annotator shortcuts: Ctrl/Cmd+Z undo, Delete/Backspace remove the selection, one letter per tool. Off while typing. */
export function useAnnotatorKeys(options: Options): void {
  const latest = useRef(options);
  latest.current = options;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const { enabled, tools, onTool, onUndo, onDelete } = latest.current;
      if (!enabled || typing(event.target)) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        onUndo();
      } else if ((event.key === 'Delete' || event.key === 'Backspace') && onDelete) {
        event.preventDefault();
        onDelete();
      } else if (!event.metaKey && !event.ctrlKey && !event.altKey) {
        const tool = tools.find((entry) => entry.key === event.key.toUpperCase());
        if (tool) onTool(tool.id);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
